import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { toast } from 'react-toastify';
import "../Package/PackageForm.css"; // Shared CSS with Includes

const HolidayExclude = ({ holidayId, packageDetails, onUpdate }) => {
  const { user } = useAuth();
  
  // State Management
  const [lists, setLists] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  
  // -- State for Adding New Item --
  const [newItemText, setNewItemText] = useState("");

  // -- State for Inline Editing --
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // -- State for Deletion --
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // 1. Load and Parse Data from Prop
  useEffect(() => {
    // Key from your console log is 'holidayExclusions'
    const rawData = packageDetails?.holidayExclusions;

    if (rawData) {
        try {
            const parsed = typeof rawData === 'string'
                ? JSON.parse(rawData)
                : rawData;

            // Map to ensure consistent frontend structure
            const mapped = parsed.map((item, index) => ({
                packageExcludeId: item.packageExcludeId || item.PackageExcludeId || `temp-${index}`,
                description: item.description || item.Description || ""
            }));
            
            setLists(mapped);
        } catch (e) {
            console.error("JSON Parse Error", e);
            setLists([]);
        }
    } else {
        setLists([]);
    }
  }, [packageDetails]);

  // --- CORE SAVE LOGIC (Updates Holiday JSON) ---
  const saveHolidayData = async (updatedList) => {
      const formData = new FormData();
      formData.append("holidayId", holidayId);
      // Key must match backend column name: 'holidayExclusions'
      formData.append("holidayExclusions", JSON.stringify(updatedList)); 
      
      if(user?.id) formData.append("userId", user.id);

      try {
          await apiService.update(config.UPDATEHOLIDAY, formData);
          if(onUpdate) {
              onUpdate(); 
          } else {
              setLists(updatedList);
          }
          return true;
      } catch (error) {
          console.error(error);
          toast.error("Failed to update exclusions");
          return false;
      }
  };

  // --- ADD NEW ITEM ---
  const handleAddNew = async () => {
    if (!newItemText.trim()) {
        toast.warning("Please enter a description");
        return;
    }
    setBtnLoading(true);

    const newItem = {
        packageExcludeId: crypto.randomUUID(),
        description: newItemText
    };

    const newList = [...lists, newItem];
    const success = await saveHolidayData(newList);
    
    if (success) {
        setNewItemText(""); 
        
    }
    setBtnLoading(false);
  };

  // --- EDIT EXISTING ITEM ---
  const startEditing = (item) => {
      setEditingId(item.packageExcludeId);
      setEditingText(item.description);
  };

  const cancelEditing = () => {
      setEditingId(null);
      setEditingText("");
  };

  const saveEditing = async () => {
      if (!editingText.trim()) {
          toast.warning("Description cannot be empty");
          return;
      }
      setBtnLoading(true);

      const newList = lists.map(item => 
          item.packageExcludeId === editingId 
          ? { ...item, description: editingText } 
          : item
      );

      const success = await saveHolidayData(newList);
      if (success) {
          setEditingId(null);
          setEditingText("");
         
      }
      setBtnLoading(false);
  };

  // --- DELETE ITEM ---
  const handleDelete = async () => {
    if (itemToDelete !== null) {
      const newList = lists.filter(x => x.packageExcludeId !== itemToDelete);
      const success = await saveHolidayData(newList);
      if(success) 
      handleCloseDeleteModal();
    }
  };

  const handleOpenDeleteModal = (id) => { setItemToDelete(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setShowDeleteModal(false); setItemToDelete(null); };

  return (
   <div className="PackageInclude-wrapper">
      <div className="PackageInclude-card">
        <div className="PackageInclude-header">
          <h4 className="header-title">Exclusions</h4>
          <p className="header-subtitle">Add or edit package exclusions below</p>
        </div>

        <div className="PackageInclude-list">
          {lists.length > 0 ? (
            lists.map((item) => (
              <div key={item.packageExcludeId} className="PackageInclude-list-item">
                <i className="ri-close-circle-line item-icon" />
                
                {/* --- INLINE EDIT MODE --- */}
                {editingId === item.packageExcludeId ? (
                  <>
                    <input
                      type="text"
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="item-edit-input"
                      autoFocus
                    />
                    <div className="item-actions">
                      <button
                        className="PackageInclude-action-btn save"
                        onClick={saveEditing}
                        disabled={btnLoading}
                      >
                        <i className="ri-check-line"></i>
                      </button>
                      <button
                        className="PackageInclude-action-btn cancel"
                        onClick={cancelEditing}
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  </>
                ) : (
                  /* --- VIEW MODE --- */
                  <>
                    <p className="item-description">{item.description}</p>
                    <div className="item-actions">
                      <button
                        className="PackageInclude-action-btn edit"
                        onClick={() => startEditing(item)}
                      >
                        <i className="ri-pencil-fill"></i>
                      </button>
                      <button
                        className="PackageInclude-action-btn delete"
                        onClick={() => handleOpenDeleteModal(item.packageExcludeId)}
                      >
                        <i className="ri-delete-bin-fill"></i>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          ) : (
            <div className="PackageInclude-empty-state">
              <i className="ri-inbox-line empty-icon"></i>
              <h5>No exclusions found</h5>
            </div>
          )}
        </div>

        {/* --- ADD NEW ROW --- */}
        <div className="PackageInclude-list-item mt-3">
          <i className="ri-add-circle-line item-icon"></i>
          <input
            type="text"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            placeholder="Add a new exclusion..."
            className="form-control"
            onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
          />
          <button
            className="PackageInclude-action-btn save"
            onClick={handleAddNew}
            disabled={btnLoading}
          >
            <i className="ri-check-line"></i>
          </button>
        </div>
      </div>

      <DeleteAlert
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default HolidayExclude;